/**
 * Shift-end confirmation (#532) — employee prompt + HR review.
 */

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '@/lib/api';
import { queryKeys } from './queryKeys';

const mineKey = ['hr', 'shift-end-confirmation', 'mine'] as const;
const reviewKey = ['hr', 'shift-end-confirmations'] as const;

function invalidateShiftEndQueries(queryClient: ReturnType<typeof useQueryClient>): void {
  void queryClient.invalidateQueries({ queryKey: mineKey });
  void queryClient.invalidateQueries({ queryKey: queryKeys.hr.timeTracking.all });
  void queryClient.invalidateQueries({ queryKey: reviewKey });
}

export interface ShiftEndConfirmation {
  id: string;
  employeeId: string;
  clockInEntryId: string;
  scheduledShiftEnd: string;
  status: 'pending' | 'continued' | 'stopped' | 'auto_closed' | 'superseded';
  responseSource: 'employee' | 'timeout' | 'manual_clock_out' | null;
  extensionMinutes: number | null;
  explanation: string | null;
  correctedClockOutAt?: string | null;
  needsEmployeeExplanation: boolean;
  needsReview: boolean;
  clockOutEntryId: string | null;
  continuedToId: string | null;
  reviewedBy: string | null;
  reviewedAt: string | null;
  reviewNotes: string | null;
  promptedAt: string;
  respondedAt: string | null;
  createdAt: string;
  updatedAt: string;
  employeeName?: string | null;
}

export interface ShiftEndConfirmationListResponse {
  data: ShiftEndConfirmation[];
}

export function useMyShiftEndConfirmation() {
  return useQuery({
    queryKey: mineKey,
    queryFn: () =>
      api.get<{ confirmation: ShiftEndConfirmation | null }>('/hr/time-tracking/shift-end-confirmation'),
    select: (res) => res.confirmation,
    refetchInterval: 15_000,
  });
}

export function useRespondShiftEndConfirmation() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: {
      id: string;
      action: 'continue' | 'stop';
      extensionMinutes?: number;
      explanation?: string;
    }) =>
      api.post<{ confirmation: ShiftEndConfirmation }>(
        `/hr/time-tracking/shift-end-confirmation/${input.id}/respond`,
        input.action === 'continue'
          ? {
              action: 'continue' as const,
              extensionMinutes: input.extensionMinutes,
              explanation: input.explanation,
            }
          : { action: 'stop' as const, explanation: input.explanation },
      ),
    onSuccess: () => {
      invalidateShiftEndQueries(queryClient);
    },
  });
}

export function useExplainShiftEndAutoClose() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: { id: string; explanation: string; clockOutAt?: string }) =>
      api.post<{ confirmation: ShiftEndConfirmation }>(
        `/hr/time-tracking/shift-end-confirmation/${input.id}/explain`,
        { explanation: input.explanation, ...(input.clockOutAt ? { clockOutAt: input.clockOutAt } : {}) },
      ),
    onSuccess: () => {
      invalidateShiftEndQueries(queryClient);
    },
  });
}

export function useShiftEndConfirmations(filters: {
  needsReview?: boolean;
  status?: ShiftEndConfirmation['status'];
}) {
  return useQuery({
    queryKey: [...reviewKey, filters] as const,
    queryFn: () =>
      api.get<ShiftEndConfirmationListResponse>('/hr/time-tracking/shift-end-confirmations', {
        params: {
          needsReview: filters.needsReview === false ? 'false' : 'true',
          ...(filters.status ? { status: filters.status } : {}),
        },
      }),
    select: (res) => res.data,
  });
}

export function useReviewShiftEndConfirmation() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: { id: string; notes?: string }) =>
      api.post<{ confirmation: ShiftEndConfirmation }>(
        `/hr/time-tracking/shift-end-confirmations/${input.id}/review`,
        { notes: input.notes },
      ),
    onSuccess: () => {
      invalidateShiftEndQueries(queryClient);
    },
  });
}
